import { useState, useEffect } from "react";
import { replacePlaceholders, fixProseSpacing, normalizeHtmlEntities } from "../../api/helpers";

const WaiverPreview = ({ 
  waiverData, 
  signerType, 
  device, 
  waiverMode, 
  governingData,
  phValues,  
  otherMap,
  setOtherMap   // add this
}) => {
  const [answers, setAnswers] = useState({});
  const [messages, setMessages] = useState({});



  useEffect(() => {
    setAnswers({});
    setMessages({});
  }, [signerType, waiverMode, waiverData]);
  
  useEffect(() => {
    questions.forEach((q, i) => {
      const key = `q_${i}`;
      const options = q[getKey("options")] || [];

      const otherOpt = options.find(
        o => o.label?.trim().toLowerCase() === "other"
      );

      if (!otherOpt) return;

      const isSelected =
        q.fieldType === "checkbox"
          ? (answers[key] || []).some(o => o.label === otherOpt.label)
          : answers[key]?.label === otherOpt.label;

      if (!isSelected) return;

      if (otherMap?.[q._id]?.trim()) return;

      setOtherMap(prev => ({
        ...prev,
        [q._id]: "Other"
      }));
    });
  }, [answers]);

  if (!waiverData) return null;

  
  /* Extract data before sections logic */
  const eligibility = waiverData?.eligibility?.all;

  const questions = eligibility?.questions || [];

  const serviceIntro = eligibility?.[`serviceIntro_${signerType}_${waiverMode}`];

  const warnMessage =
    eligibility?.[`serviceWarnMessage_${signerType}_${waiverMode}`];

  const blockMessage =
    eligibility?.[`serviceBlockMessage_${signerType}_${waiverMode}`];

   /*Helper to resolve dynamic keys*/
  const getKey = (base) => {
    return `${base}_${signerType}_${waiverMode}`;
  };
  
  /* If other is selected, always mandatory to enter other text, then it will apply others status of warn, contiue or block */
  const isOtherTextMissing = (q, i) => {
    const key = `q_${i}`;
    const options = q[getKey("options")] || [];

    const otherOpt = options.find(
      o => o.label?.trim().toLowerCase() === "other"
    );

    if (!otherOpt) return false;

    const isSelected =
      q.fieldType === "checkbox"
        ? (answers[key] || []).some(o => o.label === otherOpt.label)
        : answers[key]?.label === otherOpt.label;

    if (!isSelected) return false;

    return !otherMap?.[q._id]?.trim();
  };
  
   const isBlocked =
    Object.values(messages).some((m) => m?.type === "block") ||
    questions.some((q, i) => {
      const key = `q_${i}`;
      const options = q[getKey("options")] || [];

      const otherOpt = options.find(
        o => o.label?.trim().toLowerCase() === "other"
      );

      if (!otherOpt) return false;

      const isSelected =
        q.fieldType === "checkbox"
          ? (answers[key] || []).some(o => o.label === otherOpt.label)
          : answers[key]?.label === otherOpt.label;

      if (!isSelected) return false;

      // if other is block AND text is filled
      return (
        otherOpt.type === "block" &&
        otherMap?.[q._id]?.trim()
      );
    });

  const allAnswered = questions.every((q, i) => {
    const key = `q_${i}`;

    // original rule
    const answered =
      q.fieldType === "checkbox"
        ? answers[key] && answers[key].length > 0
        : !!answers[key];

    if (!answered) return false;

    // NEW RULE: other text mandatory
    if (isOtherTextMissing(q, i)) return false;

    return true;
  });

  /* Handle Answer Selection */
  const handleAnswer = (qIndex, option, checked, fieldType) => {
    const key = `q_${qIndex}`;

    if (fieldType === "checkbox") {
      setAnswers((prev) => {
        const prevOptions = prev[key] || [];

        let updatedOptions;

        if (checked) {
          updatedOptions = [...prevOptions, option];
        } else {
          updatedOptions = prevOptions.filter(
            (opt) => opt.label !== option.label
          );
        }

        // ✅ If nothing selected → clear message
        if (updatedOptions.length === 0) {
          setMessages((m) => ({ ...m, [key]: null }));
        } else {
          // evaluate highest severity
          const hasBlock = updatedOptions.some((o) => o.type === "block");
          const hasWarn = updatedOptions.some((o) => o.type === "warn");

          if (hasBlock) {
            setMessages((m) => ({
              ...m,
              [key]: { type: "block", text: blockMessage },
            }));
          } else if (hasWarn) {
            setMessages((m) => ({
              ...m,
              [key]: { type: "warn", text: warnMessage },
            }));
          } else {
            setMessages((m) => ({ ...m, [key]: null }));
          }
        }

        return {
          ...prev,
          [key]: updatedOptions,
        };
      });

    } else {
      // ✅ radio (same as before)
      setAnswers((prev) => ({
        ...prev,
        [key]: option,
      }));

      if (option?.type === "block") {
        setMessages((prev) => ({
          ...prev,
          [key]: { type: "block", text: blockMessage },
        }));
      } else if (option?.type === "warn") {
        setMessages((prev) => ({
          ...prev,
          [key]: { type: "warn", text: warnMessage },
        }));
      } else {
        setMessages((prev) => ({
          ...prev,
          [key]: null,
        }));
      }
    }

  };

  let sections = [];

  if (signerType === "adult") {
    sections =
      waiverMode === "quick"
        ? waiverData?.templates?.adultQuick?.sections || []
        : waiverData?.templates?.adultFull?.sections || [];
  } else {
    sections =
      waiverMode === "quick"
        ? waiverData?.templates?.minorQuick?.sections || []
        : waiverData?.templates?.minorFull?.sections || [];
  }

  const renderHtml = (html, highlight = false, bold = true) => {
    if (!html) return "";

    return fixProseSpacing(
      normalizeHtmlEntities(
        replacePlaceholders(html, phValues, highlight, bold)
      )
    );
  };

  // ✅ Collect unique warnings & errors ONCE
  const warningSet = new Set();
  const errorSet = new Set();

  Object.values(messages).forEach((msg) => {
    if (!msg?.text) return;

    const html = renderHtml(msg.text, phValues, false, true).trim();

    if (msg.type === "block") {
      errorSet.add(html);
    } else if (msg.type === "warn") {
      warningSet.add(html);
    }
  });

  return (
    <div className={`mx-auto ${device === "mobile" ? "max-w-sm" : "max-w-2xl"}`}>
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="text-center mb-4">
          <div className="text-lg font-semibold">Participant Waiver</div>
          <div className="text-sm text-gray-500">
            {waiverData?.waiver?.serviceId}
          </div>
        </div>

        {/* Eligibility Section Start */}

        {/* SERVICE INTRO */}
        {serviceIntro && (
          <div className="mb-6 text-sm text-gray-700 prose max-w-none">
            <div 
              dangerouslySetInnerHTML=
              {{ __html: renderHtml(serviceIntro, phValues, false, true) }} 
            />
          </div>
        )}

        {/* QUESTIONS */}
        {questions.map((q, i) => {
          const questionText = q[getKey("question")];
          const options = q[getKey("options")] || [];

          return (
            <div key={i} className="mb-6">
              <div
                className="font-medium mb-2 prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: renderHtml(questionText, phValues, false, true),
                }}
              />

              <div className="space-y-2">
                {options.map((opt, idx) => (
                  <label
                    key={idx}
                    className="flex items-center gap-2 border rounded-lg p-2 cursor-pointer hover:bg-gray-50"
                  >
                    
                    <input
                      type={q.fieldType === "checkbox" ? "checkbox" : "radio"}
                      name={`q_${i}`}
                      checked={
                        q.fieldType === "checkbox"
                          ? (answers[`q_${i}`] || []).some(o => o.label === opt.label)
                          : answers[`q_${i}`]?.label === opt.label
                      }
                      onChange={(e) =>
                        handleAnswer(i, opt, e.target.checked, q.fieldType)
                      }
                    />

                    <span
                      className="text-sm"
                      dangerouslySetInnerHTML={{
                        __html: renderHtml(opt.label, phValues, false, false),
                      }}
                    />

                    {/*Detect if selected option is “Other” */} 
                    {opt.label?.trim().toLowerCase() === "other" &&
                    (
                      (q.fieldType === "checkbox"
                        ? (answers[`q_${i}`] || []).some(o => o.label === opt.label)
                        : answers[`q_${i}`]?.label === opt.label)
                    ) && (
                      <input
                        type="text"
                        value={otherMap?.[q._id] || ""}
                        onChange={(e) =>
                          setOtherMap(prev => ({
                            ...prev,
                            [q._id]: e.target.value
                          }))
                        }
                        placeholder="Please specify"
                        className="ml-8 mt-2 w-full border rounded-md px-3 py-2 text-sm"
                      />
                    )}


                  </label>
                ))}
              </div>

              {/* MESSAGE - REMOVE the per-question message UI */}
              {/*messages[`q_${i}`] && (
                <div
                  className={`mt-2 text-sm p-2 rounded ${
                    messages[`q_${i}`].type === "block"
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: renderHtml(messages[`q_${i}`].text, phValues, false, true),
                    }}
                  />
                </div>
              )*/}
            </div>
          );
        })}
        
        {/* Eligibility Section End */}

        {/* Consolidated Warnings & Errors */}
        {(warningSet.size > 0 || errorSet.size > 0) && (
          <div className="mb-8 p-4 border rounded-lg">
            {errorSet.size > 0 && (
              <div className="mb-4">
                <h3 className="text-red-600 font-semibold mb-2">Important Errors</h3>
                {[...errorSet].map((e, i) => (
                  <div
                    key={i}
                    className="text-sm text-red-700 mb-2"
                    dangerouslySetInnerHTML={{ __html: e }}
                  />
                ))}
              </div>
            )}

            {errorSet.size === 0 && warningSet.size > 0 && (
              <div>
                <h3 className="text-yellow-700 font-semibold mb-2">
                  Important Warnings
                </h3>
                {[...warningSet].map((w, i) => (
                  <div
                    key={i}
                    className="text-sm text-yellow-700 mb-2"
                    dangerouslySetInnerHTML={{ __html: w }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/*Waiver UI*/}

        {!allAnswered && (
          <div className="mb-6 p-3 text-sm bg-gray-100 text-gray-600 rounded">
            Please answer all eligibility questions to continue.
          </div>
        )}

        {allAnswered && isBlocked && (
          <div className="mb-6 p-3 text-sm bg-red-100 text-red-700 rounded">
            Based on your responses, this service is not recommended.
          </div>
        )}

        
        {allAnswered && !isBlocked && (
          <>
            {/* All sections except last */}
            {sections.slice(0, -1).map((sec, i) => (
              <div key={i} className="mb-6">
                <div
                  className="font-medium mb-2"
                  dangerouslySetInnerHTML={{
                    __html: renderHtml(sec.title, phValues, false, true),
                  }}
                />
                <div
                  className="text-sm text-gray-700 prose max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: renderHtml(sec.content, phValues, false, true),
                  }}
                />
              </div>
            ))}

            {/* ✅ Governing Laws Section — SECOND LAST */}
            {governingData?.length > 0 && (
              <div className="mt-10">
                <h2 className="text-xl font-semibold mb-6">Governing Law</h2>

                {governingData.map((item, idx) => (
                  <div key={idx} className="mb-8">
                    <h3 className="font-semibold mb-2">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: renderHtml(
                            `${item.locationName} (${item.state})`,
                            phValues,
                            false,
                            true
                          ),
                        }}
                      />
                    </h3>

                    <div
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: renderHtml(item.clause, phValues, false, true),
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Last section stays last */}
            {sections.slice(-1).map((sec, i) => (
              <div key={`last_${i}`} className="mb-6">
                <div className="font-medium mb-2">{normalizeHtmlEntities(sec.title)}</div>
                <div
                  className="text-sm text-gray-700 prose max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: renderHtml(sec.content, phValues, false, true),
                  }}
                />
              </div>
            ))}
          </>
        )}

      </div>
    </div>
  );
};


export default WaiverPreview;